/**
 * Загрузка картинки для вставки в docx.
 *
 * Жила внутри «Отчёта министру»; вынесена, когда тот же код понадобился отчёту
 * «Топ видео». Рамка у отчётов разная, поэтому она — параметр, а не константа.
 */

export type DocxImageBox = { width: number; height: number };

export type FetchedImage = {
  data: Uint8Array;
  type: 'jpg' | 'png' | 'gif' | 'bmp';
  width: number;
  height: number;
};

function typeFromMime(mime: string): FetchedImage['type'] {
  if (mime.includes('png')) return 'png';
  if (mime.includes('gif')) return 'gif';
  if (mime.includes('bmp')) return 'bmp';
  return 'jpg';
}

/**
 * Тянем байты и читаем натуральные размеры, чтобы вписать в рамку с сохранением
 * пропорций. null — картинка не загрузилась; вызывающий рендерит блок без неё.
 *
 * Превью у обеих площадок живут на CDN с коротким сроком жизни (у TikTok —
 * шесть часов), так что промах здесь обычное дело и валить отчёт не должен.
 */
export async function fetchImageForDocx(
  url: string,
  box: DocxImageBox,
): Promise<FetchedImage | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = new Uint8Array(await blob.arrayBuffer());

    const bitmap = await createImageBitmap(blob).catch(() => null);
    const natW = bitmap?.width ?? box.width;
    const natH = bitmap?.height ?? box.height;
    bitmap?.close();

    // Вписываем по меньшему коэффициенту: вертикальный кадр упрётся в высоту.
    const scale = Math.min(box.width / natW, box.height / natH);
    return {
      data,
      type: typeFromMime(blob.type),
      width: Math.max(1, Math.round(natW * scale)),
      height: Math.max(1, Math.round(natH * scale)),
    };
  } catch {
    return null;
  }
}
